import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import Navigation from "../components/Navigation";
import { apiFetch } from "../utils/api";

interface Stats {
  totalLinks: number;
  activeLinks: number;
  totalVerifications: number;
  verifiedCount: number;
  failedCount: number;
  totalUsers?: number;
  recentVerifications?: any[];
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // Fetch links
      const linksResponse = await apiFetch("/links");

      if (!linksResponse.ok) throw new Error("Failed to fetch links");

      const linksData = await linksResponse.json();
      const links = linksData.links || [];

      // Fetch verifications
      const verificationsResponse = await apiFetch("/verifications");

      if (!verificationsResponse.ok) throw new Error("Failed to fetch verifications");

      const verificationsData = await verificationsResponse.json();
      const verifications = verificationsData.verifications || [];

      // Calculate stats
      const activeLinks = links.filter(
        (link: any) =>
          link.is_active && new Date(link.expires_at) > new Date()
      ).length;

      const verifiedCount = verifications.filter(
        (v: any) =>
          v.verification_result?.verified === true ||
          v.verification_result?.verified === "TRUE"
      ).length;

      const failedCount = verifications.length - verifiedCount;

      const statsData: Stats = {
        totalLinks: links.length,
        activeLinks,
        totalVerifications: verifications.length,
        verifiedCount,
        failedCount,
        recentVerifications: verifications.slice(0, 5),
      };

      // If admin, fetch users count
      if (user?.role === "admin") {
        try {
          const usersResponse = await apiFetch("/admin/users");

          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            statsData.totalUsers = usersData.users?.length || 0;
          }
        } catch (err) {
          console.error("Failed to fetch users:", err);
        }
      }

      setStats(statsData);
    } catch (err: any) {
      // Don't show toast for session expiration as user is being logged out
      if (err.message !== "Session expired. Please log in again.") {
        showToast(err.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    subtitle?: string;
    color?: string;
    onClick?: () => void;
  }> = ({ title, value, subtitle, color = "#004a9c", onClick }) => {
    return (
      <div
        className="link-card"
        style={{
          cursor: onClick ? "pointer" : "default",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onClick={onClick}
        onMouseEnter={(e) => {
          if (onClick) {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
          }
        }}
        onMouseLeave={(e) => {
          if (onClick) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "";
          }
        }}
      >
        <div style={{ padding: "8px 0" }}>
          <div
            style={{
              fontSize: "0.875rem",
              color: "#666",
              marginBottom: "8px",
              fontWeight: 500,
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "2rem",
              fontWeight: 700,
              color: color,
              marginBottom: subtitle ? "4px" : "0",
            }}
          >
            {value}
          </div>
          {subtitle && (
            <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "4px" }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <Navigation onLogout={logout} />
        <div className="dashboard-content">
          <div className="header">
            <h1 className="title">Loading...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="dashboard-container">
        <Navigation onLogout={logout} />
        <div className="dashboard-content">
          <div className="header">
            <h1 className="title">Dashboard</h1>
            <p className="subtitle">Unable to load statistics</p>
          </div>
        </div>
      </div>
    );
  }

  const verificationRate =
    stats.totalVerifications > 0
      ? ((stats.verifiedCount / stats.totalVerifications) * 100).toFixed(1)
      : "0";

  return (
    <div className="dashboard-container">
      <Navigation onLogout={logout} />
      <div className="dashboard-content">
        <div className="header">
          <h1 className="title">Dashboard</h1>
          <p className="subtitle">
            {user?.role === "admin"
              ? "Overview of all system statistics"
              : "Your verification statistics and activity"}
          </p>
        </div>

        <div className="dashboard-section">
          <h2>Statistics</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
              marginBottom: "32px",
            }}
          >
            <StatCard
              title={user?.role === "admin" ? "Total Links" : "My Links"}
              value={stats.totalLinks}
              subtitle={`${stats.activeLinks} active`}
              onClick={() => navigate("/links")}
            />
            <StatCard
              title={
                user?.role === "admin"
                  ? "Total Verifications"
                  : "My Verifications"
              }
              value={stats.totalVerifications}
              subtitle={`${stats.verifiedCount} verified`}
              onClick={() => navigate("/verifications")}
            />
            <StatCard
              title="Verified"
              value={stats.verifiedCount}
              subtitle={`${verificationRate}% success rate`}
              color="#28a745"
            />
            <StatCard
              title="Failed"
              value={stats.failedCount}
              subtitle={
                stats.totalVerifications > 0
                  ? `${(
                      (stats.failedCount / stats.totalVerifications) *
                      100
                    ).toFixed(1)}%`
                  : "0%"
              }
              color="#dc3545"
            />
            {user?.role === "admin" && stats.totalUsers !== undefined && (
              <StatCard
                title="Total Users"
                value={stats.totalUsers}
                subtitle="Registered users"
                onClick={() => navigate("/dashboard/users")}
              />
            )}
          </div>

          {stats.recentVerifications && stats.recentVerifications.length > 0 && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <h2>Recent Verifications</h2>
                <button
                  className="verify-button"
                  onClick={() => navigate("/verifications")}
                  style={{ width: "auto", padding: "8px 16px" }}
                >
                  View All
                </button>
              </div>
              <div>
                {stats.recentVerifications.map((verification: any) => {
                  const status =
                    verification.verification_result?.verified === true ||
                    verification.verification_result?.verified === "TRUE"
                      ? { status: "Verified", color: "active" }
                      : { status: "Failed", color: "inactive" };
                  return (
                    <div key={verification.id} className="link-card">
                      <div className="link-header">
                        <div>
                          <div
                            style={{ fontWeight: 600, marginBottom: "8px" }}
                          >
                            Ghana Card: {verification.ghana_card_number}
                          </div>
                          <div
                            className={`link-status ${status.color}`}
                            style={{
                              display: "inline-block",
                              marginRight: "8px",
                            }}
                          >
                            {status.status}
                          </div>
                          {verification.verification_type && (
                            <span
                              className="link-status inactive"
                              style={{ display: "inline-block" }}
                            >
                              {verification.verification_type.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <button
                          className="link-copy-btn"
                          onClick={() => navigate("/verifications")}
                          style={{ background: "#004a9c" }}
                        >
                          View Details
                        </button>
                      </div>
                      <div className="link-meta">
                        <div className="link-meta-item">
                          <span className="link-meta-label">Date of Birth</span>
                          <span>{verification.date_of_birth}</span>
                        </div>
                        <div className="link-meta-item">
                          <span className="link-meta-label">Verified At</span>
                          <span>
                            {new Date(
                              verification.created_at
                            ).toLocaleString()}
                          </span>
                        </div>
                        {user?.role === "admin" &&
                          verification.creator_email && (
                            <div className="link-meta-item">
                              <span className="link-meta-label">
                                Link Created By
                              </span>
                              <span>{verification.creator_email}</span>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(!stats.recentVerifications ||
            stats.recentVerifications.length === 0) && (
            <div className="empty-state">
              <p>No recent verifications found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
